import React from 'react'
import { Box, Button } from '@mui/material'
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined'

import type { HolidayImage } from '@/src/types/holidays'

import HeroCarouselWrapper from '../heroCarousel'
import HolidayImageView from '../holidayImage'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

interface PackageHeroProps {
    /** The whole gallery — the mobile carousel swipes through all of it. */
    images: HolidayImage[]
    /** Always exactly one. */
    lead: HolidayImage
    /** The stacked column beside the lead — two on desktop, none on mobile. */
    tiles: HolidayImage[]
    showAllLabel: string
    openGalleryLabel: string
    /** Index is the position within the full gallery, not within `tiles`. */
    onOpenAt: (index: number) => void
    isMobile: boolean
}

const tileSx = {
    position: 'relative' as const,
    overflow: 'hidden',
    cursor: 'pointer',
    border: 'none',
    p: 0,
    bgcolor: 'neutral.100'
}

const PackageHero: React.FC<PackageHeroProps> = ({
    images,
    lead,
    tiles,
    showAllLabel,
    openGalleryLabel,
    onOpenAt,
    isMobile
}) => {
    const showAllButton = (
        <Button
            variant="contained"
            onClick={() => onOpenAt(0)}
            startIcon={<PhotoLibraryOutlinedIcon />}
            sx={{
                position: 'absolute',
                bottom: 16,
                insetInlineEnd: 16,
                zIndex: 1,
                borderRadius: 999,
                px: 2.5,
                py: 1,
                fontWeight: 700,
                textTransform: 'none',
                bgcolor: 'background.paper',
                color: 'textColors.primary',
                boxShadow: 3,
                '&:hover': { bgcolor: 'neutral.100' }
            }}
        >
            {showAllLabel}
        </Button>
    )

    // Phones get a full-bleed square carousel with dot indicators, matching the reference; the
    // "show all" button is redundant there because every photo is one swipe away.
    if (isMobile) {
        return <HeroCarouselWrapper images={images} onOpenAt={onOpenAt} />
    }

    if (!tiles.length) {
        return (
            <Box sx={{ position: 'relative' }}>
                <Box
                    component="button"
                    type="button"
                    onClick={() => onOpenAt(0)}
                    aria-label={openGalleryLabel}
                    sx={{
                        ...tileSx,
                        display: 'block',
                        width: '100%',
                        height: 380,
                        borderRadius: HOLIDAYS_RADIUS.SURFACE
                    }}
                >
                    <HolidayImageView image={lead} lazy={false} isMobile={isMobile} />
                </Box>
                {showAllButton}
            </Box>
        )
    }

    return (
        <Box sx={{ position: 'relative' }}>
            {/*
             * A large lead beside a 2x2 of tiles. The columns are sized so the lead keeps roughly
             * half the width however many tiles arrive, and the grid collapses to whatever is
             * there: three photos fill the first column of tiles and leave the second empty.
             */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: tiles.length > 2 ? '1.6fr 1fr 1fr' : '2fr 1fr',
                    gridTemplateRows: 'repeat(2, 190px)',
                    gap: 1.5,
                    borderRadius: HOLIDAYS_RADIUS.SURFACE,
                    overflow: 'hidden'
                }}
            >
                <Box
                    component="button"
                    type="button"
                    onClick={() => onOpenAt(0)}
                    aria-label={openGalleryLabel}
                    sx={{ ...tileSx, gridRow: 'span 2' }}
                >
                    <HolidayImageView image={lead} lazy={false} isMobile={isMobile} />
                </Box>

                {tiles.map((image, index) => (
                    <Box
                        key={image.url}
                        component="button"
                        type="button"
                        // +1 because the lead occupies index 0 of the gallery.
                        onClick={() => onOpenAt(index + 1)}
                        aria-label={openGalleryLabel}
                        sx={tileSx}
                    >
                        <HolidayImageView image={image} isMobile={isMobile} />
                    </Box>
                ))}
            </Box>

            {showAllButton}
        </Box>
    )
}

export { PackageHero }
export type { PackageHeroProps }
export default PackageHero
